/**
 * "Kontrollera arkivet" (Mer › Lagring › Google Drive): stat every receipt referenced by
 * a verifikation, report what is missing, and re-upload from the local blob
 * when we still have one.
 */
import { exists, getRemoteStore } from '@/cloud/store';
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { Attachment, Uuid, Verifikation } from '@/domain/types';
import { uploadReceiptCopy } from './receipts';

export interface MissingAttachment {
  verifikationId: Uuid;
  /** Local attachment id when the reference resolves to one. */
  attachmentId?: Uuid;
  /** Remote id we looked for, if any. */
  driveFileId?: string;
}

export interface ArchiveCheckResult {
  total: number;
  present: number;
  missing: MissingAttachment[];
}

export interface ArchiveCheckDeps {
  exists: (fileId: string) => Promise<boolean>;
  concurrency: number;
}

const defaultDeps: ArchiveCheckDeps = { exists: (fileId) => exists(getRemoteStore(), fileId), concurrency: 4 };

/** "312 av 312 kvitton finns i arkivet" / "3 av 312 kvitton saknas i arkivet". */
export function archiveSummary(r: ArchiveCheckResult): string {
  if (r.total === 0) return 'Inga kvitton att kontrollera ännu.';
  if (r.missing.length === 0) return `${r.present} av ${r.total} kvitton finns i arkivet`;
  return `${r.missing.length} av ${r.total} kvitton saknas i arkivet`;
}

/** Resolve each attachment reference to the remote id to check; pure. */
export function resolveReferences(
  verifikationer: readonly Verifikation[],
  attachments: ReadonlyMap<string, Pick<Attachment, 'id' | 'driveFileId'>>,
): { verifikationId: Uuid; attachmentId?: Uuid; driveFileId?: string }[] {
  const refs: { verifikationId: Uuid; attachmentId?: Uuid; driveFileId?: string }[] = [];
  for (const v of verifikationer) {
    for (const ref of v.attachmentIds) {
      const local = attachments.get(ref);
      if (local) {
        refs.push({ verifikationId: v.id, attachmentId: local.id, ...(local.driveFileId ? { driveFileId: local.driveFileId } : {}) });
      } else {
        // Not a local attachment: the reference is a remote id itself.
        refs.push({ verifikationId: v.id, driveFileId: ref });
      }
    }
  }
  return refs;
}

async function mapLimit<T, R>(items: readonly T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i] as T);
    }
  });
  await Promise.all(workers);
  return results;
}

export async function checkArchive(companyId: Uuid, db: BookkeepingDB = defaultDb, deps: ArchiveCheckDeps = defaultDeps): Promise<ArchiveCheckResult> {
  const verifikationer = await db.verifikationer.where('companyId').equals(companyId).toArray();
  const refIds = [...new Set(verifikationer.flatMap((v) => v.attachmentIds))];
  const attachmentRows = refIds.length ? await db.attachments.where('id').anyOf(refIds).toArray() : [];
  const attachments = new Map(attachmentRows.map((a) => [a.id, a]));
  const refs = resolveReferences(verifikationer, attachments);

  const cache = new Map<string, Promise<boolean>>();
  const check = (fileId: string) => {
    let p = cache.get(fileId);
    if (!p) {
      p = deps.exists(fileId);
      cache.set(fileId, p);
    }
    return p;
  };
  const presentFlags = await mapLimit(refs, deps.concurrency, async (ref) => (ref.driveFileId ? check(ref.driveFileId) : false));

  const missing: MissingAttachment[] = [];
  let present = 0;
  refs.forEach((ref, i) => {
    if (presentFlags[i]) present++;
    else missing.push(ref);
  });
  return { total: refs.length, present, missing };
}

export interface ReuploadResult {
  uploaded: number;
  /** References with no local copy to upload from. */
  skipped: MissingAttachment[];
  errors: { attachmentId: Uuid; error: string }[];
}

/** Gross amount of a verifikation: the sum of its debit side. */
export function grossAmountOf(v: Pick<Verifikation, 'rows'>): number {
  return v.rows.reduce((sum, r) => sum + r.debit, 0);
}

/**
 * [Ladda upp igen]: upload missing receipts from their local blobs straight
 * into Kvitton/<år>/ with the proper name and properties.
 */
export async function reuploadMissing(companyId: Uuid, missing: readonly MissingAttachment[], db: BookkeepingDB = defaultDb): Promise<ReuploadResult> {
  const result: ReuploadResult = { uploaded: 0, skipped: [], errors: [] };
  const seen = new Set<Uuid>();
  for (const m of missing) {
    if (!m.attachmentId || seen.has(m.attachmentId)) {
      if (!m.attachmentId) result.skipped.push(m);
      continue;
    }
    seen.add(m.attachmentId);
    const [attachment, verifikation] = await Promise.all([db.attachments.get(m.attachmentId), db.verifikationer.get(m.verifikationId)]);
    if (!attachment?.blob || !verifikation) {
      result.skipped.push(m);
      continue;
    }
    try {
      const file = await uploadReceiptCopy(
        {
          companyId,
          verifikation,
          counterparty: verifikation.meta?.counterparty ?? verifikation.description,
          amountOre: grossAmountOf(verifikation),
          blob: attachment.blob,
          mediaType: attachment.mediaType,
        },
        db,
      );
      await db.attachments.update(attachment.id, { driveFileId: file.id });
      result.uploaded++;
    } catch (e) {
      result.errors.push({ attachmentId: attachment.id, error: e instanceof Error ? e.message : String(e) });
    }
  }
  return result;
}
